import moment from "moment-timezone";

export const GYM_VISITS_RANGES = ["weeks12", "weeks4", "days30"];

const CHART = { width: 860, height: 170, pad: 24, top: 20, bottom: 26 };
const MIN_SCALE = 4;

export const doorLogsHref = ({ memberId, memberName, range = "30d" }) =>
  `/coaching-portal/door-access?${new URLSearchParams({
    tab: "logs",
    memberId,
    memberName,
    range,
  })}`;

export const formatLastIn = (value, timeZone, withWeekday = false) => {
  if (!value) return "";
  const pattern = withWeekday ? "ddd, MMM D · h:mm a" : "MMM D · h:mm a";
  return moment.tz(value, timeZone).format(pattern);
};

/** How long ago, in gym-local calendar days: {key, count}. */
export const daysAgo = (value, timeZone, now = new Date()) => {
  const day = moment.tz(value, timeZone).startOf("day");
  const count = moment.tz(now, timeZone).startOf("day").diff(day, "days");
  if (count <= 0) return { key: "today" };
  if (count === 1) return { key: "yesterday" };
  return { key: "daysAgo", count };
};

/** Last in per member id, as epoch ms (0 when the member has no visit). */
export const lastInByMember = (members = []) =>
  Object.fromEntries(
    members.map(({ memberId, lastIn }) => [memberId, Date.parse(lastIn) || 0])
  );

export const compareWindow = (current, previous) => {
  if (current > previous) return "up";
  if (current < previous) return "down";
  return "same";
};

/**
 * Bars of the 12-week chart. `weeks` are rolling 7-day buckets, oldest first
 * (the API's `weeks`); a week with no visits is flagged `empty` and drawn as
 * a dashed placeholder instead of an invisible bar.
 */
export const chartBars = (weeks, avgPerWeek, timeZone) => {
  const { width, height, pad, top, bottom } = CHART;
  const peak = Math.max(0, ...weeks.map((week) => week.visits), avgPerWeek);
  const scale = Math.max(MIN_SCALE, Math.ceil(peak / 2) * 2);
  const y = (value) =>
    height - bottom - (value / scale) * (height - bottom - top);
  const slot = (width - pad) / weeks.length;
  const last = weeks.length - 1;

  return {
    width,
    height,
    baseline: y(0),
    emptyTop: y(Math.min(3, scale)),
    average: y(avgPerWeek),
    ticks: [0, scale / 2, scale].map((value) => ({ value, y: y(value) })),
    bars: weeks.map((week, index) => {
      const end = moment.tz(week.endsAt, timeZone);
      return {
        x: pad + index * slot + slot * 0.2,
        width: slot * 0.6,
        center: pad + index * slot + slot / 2,
        y: y(week.visits),
        height: y(0) - y(week.visits),
        visits: week.visits,
        empty: week.visits === 0,
        current: index === last,
        label: index === last ? null : end.format("MMM D"),
        from: end.clone().subtract(6, "days").format("MMM D"),
        to: end.format("MMM D"),
      };
    }),
  };
};
